// path: src/data/storyLevels.js

export const STORY_WORLDS = [
    {
        id: 1,
        title: "Learn the Mix",
        description: "Master the core memory rule.",
    },
    {
        id: 2,
        title: "Bigger Memories",
        description: "Grow the board and build stamina.",
    },
    {
        id: 3,
        title: "No Second Chances",
        description: "Survive Hardcore on the 3×3 board.",
    },
    {
        id: 4,
        title: "Master Mix",
        description: "Face the full board, then Hardcore.",
    },
    {
        id: 5,
        title: "The Underworld",
        description: "Modifiers arrive one by one, then mix.",
    },
];

const NO_MODIFIERS = {
    twistedMode: false,
    shyCorgisMode: false,
    anxiousMode: false,
    hardcoreMode: false,
    blitzMode: false,
};

const DEFAULT_TUTORIAL = {
    forgivingMistakes: false,
    twistedCardLimit: 0,
    maxHiddenCards: null,
    shyStartDelayMs: null,
    hardcorePreview: false,
    guidedTutorial: false,
    houseHeartContinue: false,
};

/**
 * Each Story level is a designed board run.
 * boardSize is card count: 4 (2x2), 9 (3x3), 16 (4x4).
 * clearTarget = how many full board clears finish the level.
 * Hearts-on mistakes cost a heart but keep your picks.
 * Hardcore = one wrong pick ends the run (no hearts).
 */
export const STORY_LEVELS = [
    {
        id: 1,
        worldId: 1,
        title: "First Mix",
        description:
            "Guided lesson. Follow the glowing card — pick each card once.",
        mechanic: "None",
        boardSize: 4,
        clearTarget: 1,
        requirePerfectClears: false,
        objectiveSummary: "Finish the guided lesson",
        modifiers: { ...NO_MODIFIERS },
        tutorialBehavior: {
            ...DEFAULT_TUTORIAL,
            forgivingMistakes: true,
            guidedTutorial: true,
        },
        unlocks: [],
        unlockSummary: "Unlocks the next Story level",
    },
    {
        id: 2,
        worldId: 1,
        title: "Follow the Shuffle",
        description:
            "Your turn. Clear the board once. Mistakes are free — no hearts spent.",
        mechanic: "None",
        boardSize: 4,
        clearTarget: 1,
        requirePerfectClears: false,
        objectiveSummary: "Clear the board once",
        modifiers: { ...NO_MODIFIERS },
        tutorialBehavior: {
            ...DEFAULT_TUTORIAL,
            forgivingMistakes: true,
        },
        unlocks: [],
        unlockSummary: "Unlocks the Store and the next Story level",
    },
    {
        id: 3,
        worldId: 1,
        title: "Heart Check",
        description:
            "Hearts are on. A repeat costs a heart, but your picks stay — keep going.",
        mechanic: "Hearts",
        boardSize: 4,
        clearTarget: 1,
        requirePerfectClears: false,
        objectiveSummary: "Clear the board once",
        modifiers: { ...NO_MODIFIERS },
        tutorialBehavior: {
            ...DEFAULT_TUTORIAL,
            houseHeartContinue: true,
        },
        unlocks: ["endlessBoard4", "endlessHighScore"],
        unlockSummary: "Unlocks Endless 2×2 and High Score tracking",
    },
    {
        id: 4,
        worldId: 2,
        title: "Bigger Board",
        description: "Remember nine cards on a 3×3 board.",
        mechanic: "3×3 board",
        boardSize: 9,
        clearTarget: 1,
        requirePerfectClears: false,
        objectiveSummary: "Clear the board once",
        modifiers: { ...NO_MODIFIERS },
        tutorialBehavior: { ...DEFAULT_TUTORIAL },
        unlocks: ["endlessBoard9"],
        unlockSummary: "Unlocks Endless 3×3",
    },
    {
        id: 5,
        worldId: 2,
        title: "Second Pass",
        description: "Clear the 3×3 board twice. Hearts stay on.",
        mechanic: "3×3 stamina",
        boardSize: 9,
        clearTarget: 2,
        requirePerfectClears: false,
        objectiveSummary: "Clear the board twice",
        modifiers: { ...NO_MODIFIERS },
        tutorialBehavior: { ...DEFAULT_TUTORIAL },
        unlocks: [],
        unlockSummary: "Unlocks the next Story level",
    },
    {
        id: 6,
        worldId: 2,
        title: "Memory Marathon",
        description: "Three full 3×3 clears. Stay sharp — hearts still save you.",
        mechanic: "3×3 stamina",
        boardSize: 9,
        clearTarget: 3,
        requirePerfectClears: false,
        objectiveSummary: "Clear the board 3 times",
        modifiers: { ...NO_MODIFIERS },
        tutorialBehavior: { ...DEFAULT_TUTORIAL },
        unlocks: [],
        unlockSummary: "Unlocks the next Story level",
    },
    {
        id: 7,
        worldId: 3,
        title: "Last Look",
        description: "Two more 3×3 clears with hearts before Hardcore.",
        mechanic: "3×3 stamina",
        boardSize: 9,
        clearTarget: 2,
        requirePerfectClears: false,
        objectiveSummary: "Clear the board twice",
        modifiers: { ...NO_MODIFIERS },
        tutorialBehavior: { ...DEFAULT_TUTORIAL },
        unlocks: [],
        unlockSummary: "Unlocks the next Story level",
    },
    {
        id: 8,
        worldId: 3,
        title: "Hardcore Hearts",
        description:
            "No hearts. One wrong repeat ends the attempt on the 3×3 board.",
        mechanic: "3×3 Hardcore",
        boardSize: 9,
        clearTarget: 1,
        requirePerfectClears: false,
        objectiveSummary: "Clear the board once",
        modifiers: { ...NO_MODIFIERS, hardcoreMode: true },
        tutorialBehavior: { ...DEFAULT_TUTORIAL },
        unlocks: ["hardcoreMode"],
        unlockSummary: "Unlocks Hardcore for Endless",
    },
    {
        id: 9,
        worldId: 4,
        title: "Full Board",
        description: "Face the full 4×4 memory challenge. Hearts are back.",
        mechanic: "4×4 board",
        boardSize: 16,
        clearTarget: 1,
        requirePerfectClears: false,
        objectiveSummary: "Clear the board once",
        modifiers: { ...NO_MODIFIERS },
        tutorialBehavior: { ...DEFAULT_TUTORIAL },
        unlocks: ["endlessBoard16"],
        unlockSummary: "Unlocks Endless 4×4",
    },
    {
        id: 10,
        worldId: 4,
        title: "Deep Memory",
        description: "Clear the 4×4 board twice. Hearts can still save a run.",
        mechanic: "4×4 stamina",
        boardSize: 16,
        clearTarget: 2,
        requirePerfectClears: false,
        objectiveSummary: "Clear the board twice",
        modifiers: { ...NO_MODIFIERS },
        tutorialBehavior: { ...DEFAULT_TUTORIAL },
        unlocks: [],
        unlockSummary: "Unlocks the next Story level",
    },
    {
        id: 11,
        worldId: 4,
        title: "Long Haul",
        description: "Three 4×4 clears. Then Hardcore waits at the summit.",
        mechanic: "4×4 stamina",
        boardSize: 16,
        clearTarget: 3,
        requirePerfectClears: false,
        objectiveSummary: "Clear the board 3 times",
        modifiers: { ...NO_MODIFIERS },
        tutorialBehavior: { ...DEFAULT_TUTORIAL },
        unlocks: [],
        unlockSummary: "Unlocks the next Story level",
    },
    {
        id: 12,
        worldId: 4,
        title: "Overworld Finale",
        description:
            "Hardcore on the full 4×4 board. One mistake ends the Story.",
        mechanic: "4×4 Hardcore",
        boardSize: 16,
        clearTarget: 1,
        requirePerfectClears: false,
        objectiveSummary: "Clear the board once",
        modifiers: { ...NO_MODIFIERS, hardcoreMode: true },
        tutorialBehavior: { ...DEFAULT_TUTORIAL },
        unlocks: ["fullCustomEndless", "blitzMode", "storyHardMode"],
        unlockSummary:
            "Story complete — unlocks full custom Endless, Blitz, Hard Mode, the Underworld, World Journey, and Savior of the Overworld",
    },
    {
        id: 13,
        worldId: 5,
        title: "Twisted Gates",
        description: "Every card can rotate after a shuffle.",
        mechanic: "4×4 Twisted",
        boardSize: 16,
        clearTarget: 1,
        requirePerfectClears: false,
        objectiveSummary: "Clear the board once",
        isBonusLevel: true,
        modifiers: { ...NO_MODIFIERS, twistedMode: true },
        tutorialBehavior: { ...DEFAULT_TUTORIAL },
        unlocks: ["twistedMode"],
        unlockSummary: "Unlocks Twisted Mode for Endless",
    },
    {
        id: 14,
        worldId: 5,
        title: "Shy Veil",
        description: "Cards flip face-down while you play.",
        mechanic: "4×4 Shy",
        boardSize: 16,
        clearTarget: 1,
        requirePerfectClears: false,
        objectiveSummary: "Clear the board once",
        isBonusLevel: true,
        modifiers: { ...NO_MODIFIERS, shyCorgisMode: true },
        tutorialBehavior: { ...DEFAULT_TUTORIAL },
        unlocks: ["shyCorgisMode"],
        unlockSummary: "Unlocks Shy Corgis for Endless",
    },
    {
        id: 15,
        worldId: 5,
        title: "Anxious Pulse",
        description:
            "The board scrambles on its own. Cards jiggle before each mix-up.",
        mechanic: "4×4 Anxious",
        boardSize: 16,
        clearTarget: 1,
        requirePerfectClears: false,
        objectiveSummary: "Clear the board once",
        isBonusLevel: true,
        modifiers: { ...NO_MODIFIERS, anxiousMode: true },
        tutorialBehavior: { ...DEFAULT_TUTORIAL },
        unlocks: ["anxiousMode"],
        unlockSummary: "Unlocks Anxious for Endless",
    },
    {
        id: 16,
        worldId: 5,
        title: "Ember Mix",
        description: "Twisted rotations and shy hides together.",
        mechanic: "4×4 Twisted + Shy",
        boardSize: 16,
        clearTarget: 1,
        requirePerfectClears: false,
        objectiveSummary: "Clear the board once",
        isBonusLevel: true,
        modifiers: {
            ...NO_MODIFIERS,
            twistedMode: true,
            shyCorgisMode: true,
        },
        tutorialBehavior: { ...DEFAULT_TUTORIAL },
        unlocks: [],
        unlockSummary: "Unlocks the next Underworld level",
    },
    {
        id: 17,
        worldId: 5,
        title: "Spinning Panic",
        description: "Twisted rotations while the board scrambles itself.",
        mechanic: "4×4 Twisted + Anxious",
        boardSize: 16,
        clearTarget: 1,
        requirePerfectClears: false,
        objectiveSummary: "Clear the board once",
        isBonusLevel: true,
        modifiers: {
            ...NO_MODIFIERS,
            twistedMode: true,
            anxiousMode: true,
        },
        tutorialBehavior: { ...DEFAULT_TUTORIAL },
        unlocks: [],
        unlockSummary: "Unlocks the next Underworld level",
    },
    {
        id: 18,
        worldId: 5,
        title: "Hidden Jitters",
        description: "Shy hides plus Anxious scrambles. Hearts still save you.",
        mechanic: "4×4 Shy + Anxious",
        boardSize: 16,
        clearTarget: 1,
        requirePerfectClears: false,
        objectiveSummary: "Clear the board once",
        isBonusLevel: true,
        modifiers: {
            ...NO_MODIFIERS,
            shyCorgisMode: true,
            anxiousMode: true,
        },
        tutorialBehavior: { ...DEFAULT_TUTORIAL },
        unlocks: [],
        unlockSummary: "Unlocks the next Underworld level",
    },
    {
        id: 19,
        worldId: 5,
        title: "The Final Flame",
        description:
            "Twisted, shy, and anxious together. Hearts are on — no timer.",
        mechanic: "4×4 Twisted + Shy + Anxious",
        boardSize: 16,
        clearTarget: 1,
        requirePerfectClears: false,
        objectiveSummary: "Clear the board once",
        isBonusLevel: true,
        modifiers: {
            ...NO_MODIFIERS,
            twistedMode: true,
            shyCorgisMode: true,
            anxiousMode: true,
        },
        tutorialBehavior: { ...DEFAULT_TUTORIAL },
        unlocks: [],
        unlockSummary:
            "Completes the Underworld — unlocks the Underworld Journey Frame and Ruler of the Underworld",
    },
];

export const STORY_MAIN_LEVEL_COUNT = 12;

export function getStoryLevelById(levelId) {
    return STORY_LEVELS.find((level) => level.id === Number(levelId)) || null;
}

export function getStoryLevelsForWorld(worldId) {
    return STORY_LEVELS.filter((level) => level.worldId === Number(worldId));
}

export function getStoryMainLevelCount() {
    return STORY_MAIN_LEVEL_COUNT;
}

export function getStoryBonusLevelCount() {
    return Math.max(0, STORY_LEVELS.length - STORY_MAIN_LEVEL_COUNT);
}

export function isUnderworldLevel(levelId) {
    return Number(levelId) > STORY_MAIN_LEVEL_COUNT;
}

export function getStoryLevelsForMapPage(pageId = "surface") {
    if (pageId === "underworld") {
        return STORY_LEVELS.filter((level) => isUnderworldLevel(level.id));
    }

    return STORY_LEVELS.filter((level) => !isUnderworldLevel(level.id));
}

export function getTotalStoryLevels() {
    return STORY_LEVELS.length;
}

/** Active modifier labels for a story level (for intro badges / HUD). */
export function getStoryLevelModifierLabels(level, { hardMode = false } = {}) {
    if (!level) {
        return [];
    }

    const modifiers = level.modifiers || {};
    const labels = [];

    if (modifiers.blitzMode) {
        labels.push("Blitz");
        labels.push("Hardcore");
    } else if (modifiers.hardcoreMode || hardMode) {
        labels.push("Hardcore");
    }
    if (modifiers.twistedMode) {
        labels.push("Twisted");
    }
    if (modifiers.shyCorgisMode) {
        labels.push("Shy");
    }
    if (modifiers.anxiousMode) {
        labels.push("Anxious");
    }

    return labels;
}
